import AddTool, { AddToolContext } from './add-tool.js';
import GenericAssetView from '../../../views/generic-asset-view.js';
import SectionView from '../../../views/section-view.js';
import Signal from '../../../control-flow/signal.js';
import logging from '../../../logging.js';
import visibleSize from '../../../view3d/visible-size.js';
import { Box3, Vector3 } from 'three';
import { GenericAsset, Group, Section } from '../../../domain.js';
import { getParentView } from '../../../controllers/base-view-controllers.js';
import { overlapsSectionContent } from '../../../view3d/overlaps-section-view.js';
import { round2slot, trunc } from '../../../measurement.js';
import { PI } from '../../../math/constants.js';

const log = logging('tools');


export class AddGenericAssetTool extends AddTool
{

    /**
     * This is the section that the mouse is currently over.
     */
    target?: SectionView;

    model: GenericAsset;
    proxy: GenericAssetView;

    declare onSuccess: Signal<[GenericAsset]>;

    constructor(prototype: GenericAsset, icon: HTMLElement, ctx: AddToolContext) {
        super(icon, ctx);

        this.model = GenericAsset.fromJSON({
            availableWidths: prototype.availableWidths,
            filename: prototype.filename,
            assetId: prototype.assetId,
        });
        this.ctx.defaults.reset(this.model);

        const
            rotation = this.model.assetId?.includes('illuminated-shelf')
                ? { x: -PI/10, y: -PI/20, z: 0 }
                : {},
            group = Group.fromJSON({ rotation });

        group.assets.push(this.model);
        group.commit(true);

        this.updateThumbnail(group);
    }

    dragenter(): void {
        log.debug('AddGenericAssetTool.dragenter()');
        this.proxy = new GenericAssetView(this.model);
    }

    dragover(event: PointerEvent, target: HTMLElement): void {
        log.debug('AddGenericAssetTool.dragover()');

        this.updateWorldPosition(event, target);
        this.onDisplayChange.trigger();

        const
            y = round2slot(this.worldPosition.y),
            hit = this.ctx.picker.topHit;
        let section: Section, sectionView: SectionView;
        if(hit instanceof SectionView) {
            section = hit.model;
        } else if(hit) {
            try {
                sectionView = getParentView(hit) as SectionView;
                section = sectionView.model;
            } catch(error) {
                log.error(error);
            }
        }

        const
            box = new Box3(),
            v = new Vector3();
        box.makeEmpty();
        visibleSize(this.proxy, box);
        this.proxy.getWorldPosition(v);

        const
            height = box.max.y - box.min.y,
            newBottom = y + (box.min.y - v.y), // adjust for object origin
            xOffset = v.x - box.min.x ;

        if(hit?.userData.isSectionHit
           && this.isSectionWidthAvailable(section)
           && !overlapsSectionContent(sectionView, newBottom, height, this.proxy))
        {
            this.target = sectionView;
            sectionView.add(this.proxy);

            this.model.position.x = xOffset;
            this.model.position.y = y;
            this.model.position.z = 0;
            this.model.width = section.width;
        }
        else {
            delete this.target;

            // check if the proxy belongs to the scene ... if so, fine, else
            // remake the proxy and add it back to the scene.
            if(this.proxy.parent != this.ctx.scene) {
                this.proxy.removeFromParent();
                this.ctx.scene.add(this.proxy);
            }

            this.model.position.x = this.worldPosition.x;
            this.model.position.y = this.worldPosition.y;
            this.model.position.z = 0;
            this.model.width = 24;
        }
    }

    dragleave(): void {
        log.debug('AddGenericAssetTool.dragleave()');
        this.proxy.removeFromParent();
        this.onDisplayChange.trigger();
    }

    cancel(): void {
        log.debug('AddGenericAssetTool.cancel()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();
    }

    end(): void {
        log.debug('AddGenericAssetTool.end()');
        this.proxy.removeFromParent();
        this.onDisplayChange.trigger();

        // if we ended over a section, then we'll have a defined target
        // property, which means we can drop something.
        if(this.target) {
            const model = this.model;
            model.parent = this.target.model;
            model.position.z = 0;
            this.target.model.assets.push(model.clone(true));

            this.onSuccess.trigger(model);
        }
    }

    isSectionWidthAvailable(section: Section) {
        if(this.model.availableWidths.length > 0) {
            return this.model.availableWidths.includes(trunc(section.width, 2));
        } else {
            return trunc(section.width, 2) == 24.0;
        }
    }

}
export default AddGenericAssetTool;
